import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiX,
  FiCalendar,
  FiMapPin,
  FiUser,
  FiPhone,
  FiMail,
  FiEye,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';

import api from '../../utils/api';
import toast from 'react-hot-toast';

const RestaurantVisits = () => {
  const [visits, setVisits] = useState([]);

  // Loading States
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVisits, setTotalVisits] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Active Filter
  const [activeDateFilter, setActiveDateFilter] = useState('');

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // API Params
  const fetchParams = useMemo(() => {
    const params = {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch
    };

    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    return params;
  }, [currentPage, itemsPerPage, debouncedSearch, startDate, endDate]);

  // Fetch Visits
  const fetchVisits = useCallback(
    async (isManualRefresh = false) => {
      try {
        if (isManualRefresh) {
          setRefreshing(true);
          toast.loading('Refreshing visits...', { id: 'refresh' });
        } else if (initialLoading) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }

        const response = await api.get('/sales-officer/visits/all', {
          params: fetchParams
        });

        setVisits(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalVisits(response.data.total || 0);

        if (isManualRefresh) {
          toast.success('Visits refreshed successfully', {
            id: 'refresh'
          });
        }
      } catch (error) {
        console.error(error);

        toast.error(
          error.response?.data?.message || 'Failed to fetch visits',
          {
            id: 'refresh'
          }
        );
      } finally {
        setLoading(false);
        setTableLoading(false);
        setRefreshing(false);
        setInitialLoading(false);
      }
    },
    [fetchParams, initialLoading]
  );

  // Fetch on Filters Change
  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  // Active Date Button Sync
  useEffect(() => {
    if (startDate && endDate) {
      const today = new Date().toISOString().split('T')[0];

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const yesterdayStr = yesterday.toISOString().split('T')[0];

      if (startDate === yesterdayStr && endDate === yesterdayStr) {
        setActiveDateFilter('yesterday');
      } else if (startDate === today && endDate === today) {
        setActiveDateFilter('today');
      } else {
        setActiveDateFilter('');
      }
    } else {
      setActiveDateFilter('');
    }
  }, [startDate, endDate]);

  // Search
  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleSearchIconClick = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setCurrentPage(1);
  }, []);

  const handleSearchKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        setDebouncedSearch(search);
        setCurrentPage(1);
      }
    },
    [search]
  );

  // Date Filters
  const handleYesterdayClick = useCallback(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const yesterdayStr = yesterday.toISOString().split('T')[0];

    setStartDate(yesterdayStr);
    setEndDate(yesterdayStr);
    setCurrentPage(1);
    setActiveDateFilter('yesterday');
  }, []);

  const handleTodayClick = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];

    setStartDate(today);
    setEndDate(today);
    setCurrentPage(1);
    setActiveDateFilter('today');
  }, []);

  // View Details
  const viewDetails = (visit) => {
    setSelectedVisit(visit);
    setShowModal(true);
  };

  // Initial Full Screen Loader
  if (loading && initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

          <p className="text-gray-600 font-medium">
            Loading restaurant visits...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <FiMapPin className="text-white text-xl" />
                </div>

                Restaurant Visits
              </h1>

              <p className="text-gray-600 mt-2">
                View all restaurant visits by salespersons
              </p>
            </div>

            <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full">
              <FiMapPin className="text-blue-600" />

              <span className="font-semibold">
                {totalVisits} Total Visits
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[300px] relative">
              <input
                type="text"
                placeholder="Search by restaurant, Sales Officer, or city..."
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyPress}
                className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />

              <button
                onClick={handleSearchIconClick}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            {/* Start Date */}
            <div className="relative">
              <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-12 pr-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>

            {/* End Date */}
            <div className="relative">
              <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-12 pr-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Clear */}
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setActiveDateFilter('');
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Clear Dates
              </button>
            )}

            {/* Yesterday */}
            <button
              onClick={handleYesterdayClick}
              className={`px-4 py-3 rounded-lg font-medium ${
                activeDateFilter === 'yesterday'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Yesterday
            </button>

            {/* Today */}
            <button
              onClick={handleTodayClick}
              className={`px-4 py-3 rounded-lg font-medium ${
                activeDateFilter === 'today'
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              Today
            </button>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          {/* Small Loader */}
          {tableLoading && (
            <div className="px-6 py-3 border-b bg-blue-50 text-blue-600 text-sm font-medium flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              Loading visits...
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Restaurant
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Owner
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Location
                  </th>

                 <th className="px-6 py-4 text-left text-xs font-semibold">
                  POS Details
                </th>


                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Sales Person
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Sales Officer
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Visit Date
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {visits.map((visit) => (
                  <tr
                    key={visit._id}
                    className="hover:bg-gray-50 transition"
                  >
                    {/* Restaurant */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {visit.restaurantName || 'N/A'}
                        </span>

                        <span className="text-xs text-gray-500">
                          {visit.cuisineType || 'N/A'}
                        </span>
                      </div>
                    </td>

                    {/* Owner */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-sm">
                        <span className="font-medium">
                          {visit.ownerName}
                        </span>

                        <span className="text-gray-500 flex items-center gap-1">
                          <FiPhone className="w-3 h-3" />
                          {visit.ownerContact}
                        </span>

                        {visit.ownerEmail && (
                          <span className="text-gray-500 flex items-center gap-1">
                            <FiMail className="w-3 h-3" />
                            {visit.ownerEmail}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Location */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-sm">
                        <span className="flex items-center gap-1">
                          <FiMapPin className="w-3 h-3" />
                          {visit.city}, {visit.state}
                        </span>
                      </div>
                    </td>

                   {/* POS */}
<td className="px-6 py-4 whitespace-nowrap">
  {visit.posDetails?.posName ? (
    <div className="flex flex-col text-sm">
      <span className="font-medium text-gray-900">
        {visit.posDetails.posName}
      </span>

      {visit.posDetails.expiryDate && (
        <span
          className={`text-xs ${
            new Date(visit.posDetails.expiryDate) <
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
              ? 'text-red-600 font-semibold'
              : 'text-gray-500'
          }`}
        >
          Expires:{' '}
          {new Date(
            visit.posDetails.expiryDate
          ).toLocaleDateString()}
        </span>
      )}
    </div>
  ) : (
    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
      No POS
    </span>
  )}
</td>

                    {/* Sales Person */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-sm">
                        <span className="font-medium">
                          {visit.salesPerson?.name || 'N/A'}
                        </span>

                        <span className="text-xs text-gray-500">
                          {visit.salesPerson?.employeeId}
                        </span>
                      </div>
                    </td>

                    {/* Sales Officer */}
<td className="px-6 py-4 whitespace-nowrap">
  <div className="flex flex-col text-sm">
    <span className="font-medium text-gray-900">
      {visit.salesOfficer?.name}
    </span>

    <span className="text-xs text-gray-500">
      {visit.salesOfficer?.employeeId}
    </span>
  </div>
</td>

                    {/* Visit Date */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm">
                        <FiCalendar className="w-4 h-4 text-gray-400" />

                        {new Date(visit.visitDate).toLocaleDateString()}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() => viewDetails(visit)}
                        className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                      >
                        <FiEye className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty */}
          {visits.length === 0 && !tableLoading && (
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold text-gray-700">
                No restaurant visits found
              </h3>

              <p className="text-gray-500 mt-2">
                Try adjusting your search or filters
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 mt-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing {visits.length} of {totalVisits} visits
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg disabled:opacity-50"
                >
                  <FiChevronLeft />
                </button>

                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 rounded-lg font-medium ${
                          pageNum === currentPage
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(totalPages, prev + 1)
                    )
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg disabled:opacity-50"
                >
                  <FiChevronRight />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm">Show</span>

                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedVisit && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-2xl"
            >
              ×
            </button>

            <h2 className="text-2xl font-bold mb-6">
              Restaurant Visit Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <h3 className="font-semibold mb-2">Restaurant</h3>

                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Name:</strong>{' '}
                    {selectedVisit.restaurantName}
                  </p>

                  <p>
                    <strong>Address:</strong>{' '}
                    {selectedVisit.address}
                  </p>

                  <p>
                    <strong>City:</strong> {selectedVisit.city}
                  </p>

                  <p>
                    <strong>State:</strong> {selectedVisit.state}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Owner</h3>

                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Name:</strong>{' '}
                    {selectedVisit.ownerName}
                  </p>

                  <p>
                    <strong>Contact:</strong>{' '}
                    {selectedVisit.ownerContact}
                  </p>

                  <p>
                    <strong>Email:</strong>{' '}
                    {selectedVisit.ownerEmail || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {selectedVisit.notes && (
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Notes</h3>

                <p className="text-sm text-gray-700">
                  {selectedVisit.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantVisits;